import { trackPendoEvent } from 'services/PendoService';

import type { HeadsUpIntroDismissReason } from './HeadsUpIntroModal';

/**
 * Reach and dismissal for the one-time heads-up Sit & Go introduction
 * (EN-10748), on the multi-run introduction's pattern (EN-10791, event names
 * confirmed by Cory, who owns the data definitions): a one-shot modal's first
 * days are its whole audience, so the measurement ships with it. Every event
 * carries the edition, so each announcement can be measured apart. Production
 * stages only (the Pendo allowlist), no player identity in the payload (Pendo
 * already keys the visitor on playerGUID), only the edition id and never its
 * copy, and analytics never blocks the modal.
 *
 * Reasons worth reading apart: 'find-table' is the player taking the
 * introduction up on its offer (the button that lands on the Sit & Go tab
 * with Heads-up lit); the rest are ways of putting it away.
 */

/**
 * How a showing ended: the player's own dismissal, or 'interrupted' when the
 * app closed it without counting it as seen (live play, the location, leaving
 * the lobby, the flag or the biometric sheet). Every showing reports exactly
 * one, so shown and dismissed add up.
 */
export type HeadsUpIntroCloseReason = HeadsUpIntroDismissReason | 'interrupted';

type HeadsUpIntroEvent =
  | { name: 'heads_up_intro_shown'; edition: string }
  | { name: 'heads_up_intro_dismissed'; edition: string; reason: HeadsUpIntroCloseReason };

/** Sent through PendoService, so it waits for this player's identified visitor. */
export const reportHeadsUpIntroEvent = (event: HeadsUpIntroEvent, playerGUID: string): void => {
  const { name, ...properties } = event;
  trackPendoEvent({ playerGUID, eventName: name, properties });
};
